import { useAnimate } from '@mui/x-charts/hooks'
import { BarLabelProps } from '@mui/x-charts/BarChart'
import { styled } from '@mui/material/styles'
import { interpolateObject } from '@mui/x-charts-vendor/d3-interpolate'
import { BarChart } from '@mui/x-charts/BarChart'
import { Applicant } from 'store/dashboard/types'
import theme from 'containers/app/theme'
import { formatNumber } from 'store/utils'
import { useAdminQuery } from 'containers/dashboard/multisectorReport/hooks/useAdminQuery'

type Props = {
  dataset: Applicant[]
  xKey: keyof Applicant
  xLabel: string
  topCompaniesNumber: number
}

const getBarChartStyles = (amountOfCompanies: number) => ({
  height: amountOfCompanies ? Number(amountOfCompanies) * (40 - amountOfCompanies / 3.5) : 500,
})

const formatCompanyName = (name: string, maxLength: number = 33) => {
  // Convertir a lowercase y capitalizar primera letra de cada palabra
  const formattedName = name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

  return formattedName.length > maxLength ? `${formattedName.substring(0, maxLength)}...` : formattedName
}

const getInfoForBoAdmin = (isAdminView: boolean, company: Applicant) => {
  if (!isAdminView) return ''
  return `• whales_patent_in_percent (pure play %): ${formatNumber(company.whales_patent_in_percent)}
  • whales_patents_in_categories (ip weight): ${company.whales_patents_in_categories} 
  • intersection_score_sum: ${company.intersection_score_sum.toFixed(2)}
  • research: ${company.research}  `
}

export default function CustomChart({ dataset, xKey, xLabel, topCompaniesNumber }: Props) {
  const firstValue = Number(dataset[0][xKey])
  const secondValue = Number(dataset[1][xKey])
  // Calculate max value for xAxis to leave some space at the end of the bars
  const highestScore = firstValue > secondValue ? Number(dataset[0][xKey]) : Number(dataset[1][xKey])
  const maxValue = highestScore + highestScore / 7

  const isAdminView = useAdminQuery().isAdminView
  const styles = getBarChartStyles(topCompaniesNumber)

  const colorsArray = Array(topCompaniesNumber).fill('#36d4ae') //Yellow color for the rest of the companies
  return (
    <BarChart
      dataset={dataset as any[]}
      // {...styles}
      layout="horizontal"
      borderRadius={6}
      height={styles.height}
      slotProps={{
        tooltip: {
          sx: { whiteSpace: 'pre-line' },
        },
      }}
      yAxis={[
        {
          dataKey: 'applicant_original_name',
          width: 200,
          tickLabelStyle: {
            fill: theme.palette.grey[700],
            fontSize: 11,
          },
          valueFormatter: (value: string) => formatCompanyName(value),
          colorMap: {
            type: 'ordinal',
            colors: [theme.palette.primary.main, ...colorsArray],
          },
        },
      ]}
      xAxis={[
        {
          max: maxValue,
          label: xLabel,
          dataKey: xKey,
          disableLine: true,
          disableTicks: true,
          tickLabelStyle: {
            display: 'none',
          },
        },
      ]}
      series={[
        {
          dataKey: xKey,
          valueFormatter: (value: any, { dataIndex }) => {
            const company = dataset[dataIndex]
            return `${xKey === 'whales_patent_in_percent' ? `Pure play: ${formatNumber(value)}%` : `${formatNumber(value)}`} 
            ${getInfoForBoAdmin(isAdminView, company)}`
          },
        },
      ]}
      barLabel={(item, context) => {
        if (xKey === 'whales_patent_in_percent') {
          return `${formatNumber(item.value ?? 0)}%`
        }
        return `${formatNumber(item.value ?? 0)}`
      }}
      slots={{ barLabel: BarLabel }}
    />
  )
}

const Text = styled('text')(({ theme }) => ({
  stroke: 'none',
  fill: (theme.vars || theme)?.palette?.text?.primary,
  transition: 'opacity 0.2s ease-in, fill 0.2s ease-in',
  textAnchor: 'middle',
  dominantBaseline: 'central',
  pointerEvents: 'none',
  fontSize: 11,
  backgroundColor: 'red',
}))

function BarLabel(props: BarLabelProps) {
  const {
    seriesId,
    dataIndex,
    color,
    isFaded,
    isHighlighted,
    classes,
    xOrigin,
    yOrigin,
    x,
    y,
    width,
    height,
    layout,
    skipAnimation,
    ...otherProps
  } = props

  const animatedProps = useAnimate(
    { x: x + width + 18, y: y + 12 },
    {
      initialProps: { x: x + width / 2, y: yOrigin },
      createInterpolator: interpolateObject,
      transformProps: p => p,
      applyProps: (element: SVGTextElement, p) => {
        element.setAttribute('x', p.x.toString())
        element.setAttribute('y', p.y.toString())
      },
      skip: skipAnimation,
    },
  )

  return <Text {...otherProps} fill={color} textAnchor="middle" {...animatedProps} />
}
